import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";

import { useProperty } from "./PropertyContext";

const PageAssistantContext = createContext(null);

function safeString(value) {
  return String(value == null ? "" : value).trim();
}

function truncateText(value, maxLen) {
  const clean = safeString(value);
  const limit = Number.isFinite(Number(maxLen)) ? Number(maxLen) : 200;
  if (clean.length <= limit) return clean;
  return clean.slice(0, limit);
}

function decodeRoutePart(value) {
  try {
    return decodeURIComponent(String(value || ""));
  } catch {
    return String(value || "");
  }
}

function isElementVisible(element) {
  if (!element) return false;
  if (element.disabled) return false;
  if (element.type === "hidden") return false;

  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden") return false;
  if (Number(style.opacity || 1) <= 0) return false;

  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function normalizeFieldKey(rawKey, fallback) {
  const clean = safeString(rawKey)
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_\-:.]/g, "");
  if (clean) return clean.slice(0, 120);
  return `field_${fallback}`;
}

function fieldLooksSensitive(element, fieldKey, label) {
  const check = `${safeString(fieldKey)} ${safeString(label)} ${safeString(element?.name)} ${safeString(element?.id)}`
    .toLowerCase();
  if (!check) return false;
  return /password|api[_-]?key|token|secret|credential|ssn/.test(check);
}

function readElementValue(element) {
  if (!element) return "";
  if (element.type === "checkbox" || element.type === "radio") {
    return element.checked ? "true" : "false";
  }
  return safeString(element.value);
}

function readLabelText(element) {
  if (!element) return "";
  const ariaLabel = safeString(element.getAttribute("aria-label"));
  if (ariaLabel) return ariaLabel;

  const id = safeString(element.id);
  if (id) {
    const explicit = document.querySelector(`label[for="${CSS.escape(id)}"]`);
    if (explicit) {
      const text = safeString(explicit.textContent);
      if (text) return text;
    }
  }

  const parentLabel = element.closest("label");
  if (parentLabel) {
    const text = safeString(parentLabel.textContent);
    if (text) return text;
  }

  const placeholder = safeString(element.getAttribute("placeholder"));
  if (placeholder) return placeholder;

  return safeString(element.name || element.id || "Field");
}

function extractValidation(element) {
  if (!element) return {};
  const out = {};
  if (element.required) out.required = true;

  if (Number.isFinite(Number(element.min))) out.min = Number(element.min);
  if (Number.isFinite(Number(element.max))) out.max = Number(element.max);
  if (Number.isFinite(Number(element.maxLength)) && Number(element.maxLength) > -1) {
    out.maxLength = Number(element.maxLength);
  }

  const pattern = safeString(element.getAttribute("pattern"));
  if (pattern) out.pattern = pattern;

  return out;
}

function getEntityIdsFromRoute(pathname, search) {
  const route = safeString(pathname);
  const out = {};

  const patterns = [
    { key: "workOrderId", regex: /^\/work-orders\/([^/]+)$/i },
    { key: "taskId", regex: /^\/tasks\/([^/]+)$/i },
    { key: "suiteId", regex: /^\/property-settings\/suites\/([^/]+)$/i },
    { key: "documentId", regex: /^\/sign\/([^/]+)$/i },
    { key: "leaseId", regex: /^\/(?:lease-sign|ontario-sign|manitoba-sign)\/([^/]+)$/i },
  ];

  for (let i = 0; i < patterns.length; i += 1) {
    const hit = route.match(patterns[i].regex);
    if (!hit) continue;
    out[patterns[i].key] = truncateText(decodeRoutePart(hit[1]), 120);
  }

  const query = new URLSearchParams(search || "");
  ["leaseId", "tenantId", "unitId", "suiteId", "workOrderId", "documentId", "taskId"].forEach((key) => {
    const value = truncateText(query.get(key), 120);
    if (value) out[key] = value;
  });

  return out;
}

function writeValueToElement(element, nextValue) {
  if (!element) return false;

  if (element.type === "checkbox" || element.type === "radio") {
    const truthy = /^(1|true|yes|on)$/i.test(String(nextValue || "").trim());
    element.checked = truthy;
  } else if (element.tagName === "SELECT") {
    element.value = String(nextValue ?? "");
  } else {
    element.value = String(nextValue ?? "");
  }

  element.dispatchEvent(new Event("input", { bubbles: true }));
  element.dispatchEvent(new Event("change", { bubbles: true }));
  return true;
}

function buildDomFieldSnapshot() {
  const nodes = Array.from(document.querySelectorAll("input, textarea, select"));
  const fieldMap = new Map();
  const fields = [];

  nodes.forEach((element, index) => {
    if (!isElementVisible(element)) return;
    if (element.type === "file") return;

    const key = normalizeFieldKey(
      element.getAttribute("data-field-key") ||
        element.name ||
        element.id,
      index
    );
    const label = truncateText(readLabelText(element), 160);

    if (fieldLooksSensitive(element, key, label)) return;

    const fieldDescriptor = {
      fieldKey: key,
      label: label || key,
      currentValue: truncateText(readElementValue(element), 800),
      validation: extractValidation(element),
    };

    if (!fieldMap.has(key)) {
      fieldMap.set(key, element);
      fields.push(fieldDescriptor);
    }
  });

  return { fields, fieldMap };
}

export function PageAssistantProvider({ children }) {
  const location = useLocation();
  const { activeProperty } = useProperty();

  const providerRegistryRef = useRef(new Map());
  const domFieldMapRef = useRef(new Map());

  const registerFieldProvider = useCallback((providerId, provider) => {
    const key = safeString(providerId);
    if (!key || !provider || typeof provider !== "object") {
      return () => {};
    }
    providerRegistryRef.current.set(key, provider);
    return () => {
      providerRegistryRef.current.delete(key);
    };
  }, []);

  const collectProviderFields = useCallback(() => {
    const merged = [];
    const seen = new Set();

    providerRegistryRef.current.forEach((provider) => {
      if (!provider || typeof provider.getFillableFields !== "function") return;
      const list = provider.getFillableFields();
      if (!Array.isArray(list)) return;
      list.forEach((row, idx) => {
        const fieldKey = normalizeFieldKey(row?.fieldKey, idx);
        if (!fieldKey || seen.has(fieldKey)) return;
        seen.add(fieldKey);
        merged.push({
          fieldKey,
          label: truncateText(row?.label || fieldKey, 160),
          currentValue: truncateText(row?.currentValue, 800),
          validation: row?.validation && typeof row.validation === "object" ? row.validation : {},
        });
      });
    });

    return merged;
  }, []);

  const getPageContext = useCallback(() => {
    const providerFields = collectProviderFields();
    const domSnapshot = buildDomFieldSnapshot();
    domFieldMapRef.current = domSnapshot.fieldMap;

    const merged = [];
    const seen = new Set();

    providerFields.forEach((row) => {
      if (!row?.fieldKey || seen.has(row.fieldKey)) return;
      seen.add(row.fieldKey);
      merged.push(row);
    });

    domSnapshot.fields.forEach((row) => {
      if (!row?.fieldKey || seen.has(row.fieldKey)) return;
      seen.add(row.fieldKey);
      merged.push(row);
    });

    return {
      routeName: location.pathname,
      propertyId: activeProperty && activeProperty !== "all" ? activeProperty : "",
      entityIds: getEntityIdsFromRoute(location.pathname, location.search),
      fillableFields: merged,
      summary: `Route ${location.pathname} with ${merged.length} fillable fields visible.`,
    };
  }, [activeProperty, collectProviderFields, location.pathname, location.search]);

  const applyFieldValue = useCallback((fieldKey, value) => {
    const key = safeString(fieldKey);
    if (!key) return false;

    const providers = Array.from(providerRegistryRef.current.values());
    for (let i = 0; i < providers.length; i += 1) {
      const provider = providers[i];
      if (!provider || typeof provider.applyFieldValue !== "function") continue;
      const applied = provider.applyFieldValue(key, value);
      if (applied) return true;
    }

    let element = domFieldMapRef.current.get(key);
    if (!element) {
      const snapshot = buildDomFieldSnapshot();
      domFieldMapRef.current = snapshot.fieldMap;
      element = domFieldMapRef.current.get(key);
    }

    if (!element) return false;
    return writeValueToElement(element, value);
  }, []);

  const applyFieldPatch = useCallback((proposals) => {
    const list = Array.isArray(proposals) ? proposals : [];
    let appliedCount = 0;
    list.forEach((proposal) => {
      if (!proposal || typeof proposal !== "object") return;
      const ok = applyFieldValue(proposal.fieldKey, proposal.value);
      if (ok) appliedCount += 1;
    });
    return appliedCount;
  }, [applyFieldValue]);

  const value = useMemo(() => ({
    getPageContext,
    applyFieldValue,
    applyFieldPatch,
    registerFieldProvider,
  }), [applyFieldPatch, applyFieldValue, getPageContext, registerFieldProvider]);

  return (
    <PageAssistantContext.Provider value={value}>
      {children}
    </PageAssistantContext.Provider>
  );
}

export function usePageAssistantContext() {
  const value = useContext(PageAssistantContext);
  if (!value) {
    throw new Error("usePageAssistantContext must be used within PageAssistantProvider");
  }
  return value;
}

export function useRegisterAssistantFieldProvider(providerId, provider) {
  const { registerFieldProvider } = usePageAssistantContext();

  useEffect(() => {
    return registerFieldProvider(providerId, provider);
  }, [provider, providerId, registerFieldProvider]);
}
